import React, { useState, useEffect } from 'react';

const API = '/api';

const st = {
  wrap: { padding: 20, maxWidth: 900, margin: '0 auto' },
  card: { backgroundColor: '#F9FAFB', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', margin: '0 0 4px 0' },
  subtitle: { fontSize: 12.5, color: '#6b7280', marginBottom: 16 },
  btn: { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: '600' },
  btnPrimary: { backgroundColor: '#1a3a6e', color: 'white' },
  btnSecondary: { backgroundColor: 'white', color: '#1a3a6e', border: '1px solid #1a3a6e' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px', backgroundColor: '#1a3a6e', color: 'white', fontSize: 12.5 },
  td: { padding: '8px 10px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'middle' },
  label: { display: 'block', fontSize: 12, fontWeight: '600', color: '#6b7280', marginBottom: 4 },
  input: { width: '100%', height: 36, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  field: { marginBottom: 12 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  logoPreview: { maxHeight: 40, maxWidth: 180, display: 'block', marginTop: 8, backgroundColor: 'white', padding: 4, border: '1px solid #e5e7eb', borderRadius: 4 },
  swatch: { display: 'inline-block', width: 16, height: 16, borderRadius: 4, border: '1px solid #d1d5db', verticalAlign: 'middle', marginRight: 6 },
  error: { color: '#dc2626', fontSize: 12.5, marginBottom: 8 },
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const FORM_VACIO = { nombre: '', color_texto_titulo: '#1a3a6e', activo: true };

// Carga la imagen del logo vía fetch (respetando el header Authorization que
// apiAuth.js agrega a las llamadas a /api/*) y la expone como blob URL — un
// <img src="/api/..."> directo no manda ese header y el endpoint (protegido
// con requireAdmin) respondería 401.
const cargarLogoBlobUrl = async (id) => {
  try {
    const r = await fetch(`${API}/hipotecarias-marca/${id}/logo`);
    if (!r.ok) return null;
    const blob = await r.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

const HipotecariasMarca = () => {
  const [lista, setLista] = useState([]);
  const [logoUrls, setLogoUrls] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const cargar = async () => {
    const r = await fetch(`${API}/hipotecarias-marca`);
    if (!r.ok) return;
    const data = await r.json();
    setLista(data);
    const conLogo = data.filter((h) => h.logo_filename);
    const entradas = await Promise.all(conLogo.map(async (h) => [h.id, await cargarLogoBlobUrl(h.id)]));
    setLogoUrls(Object.fromEntries(entradas));
  };

  useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(FORM_VACIO);
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setError('');
    setShowForm(true);
  };

  const abrirEditar = async (h) => {
    setEditando(h);
    setForm({ nombre: h.nombre, color_texto_titulo: h.color_texto_titulo, activo: h.activo });
    setLogoFile(null);
    setLogoPreviewUrl(h.logo_filename ? (logoUrls[h.id] || await cargarLogoBlobUrl(h.id)) : null);
    setError('');
    setShowForm(true);
  };

  const onLogoChange = (e) => {
    const f = e.target.files[0] || null;
    setLogoFile(f);
    if (f) setLogoPreviewUrl(URL.createObjectURL(f));
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    if (!HEX_RE.test(form.color_texto_titulo)) { setError('Color de texto del título inválido (formato hex, ej. #1a3a6e)'); return; }
    setGuardando(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('nombre', form.nombre.trim());
      fd.append('color_texto_titulo', form.color_texto_titulo);
      fd.append('activo', String(form.activo));
      if (logoFile) fd.append('logo', logoFile);
      const url = editando ? `${API}/hipotecarias-marca/${editando.id}` : `${API}/hipotecarias-marca`;
      const r = await fetch(url, { method: editando ? 'PUT' : 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Error al guardar'); return; }
      setShowForm(false);
      cargar();
    } catch {
      setError('Error de conexión');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (h) => {
    if (!window.confirm(`¿Eliminar "${h.nombre}"?`)) return;
    const r = await fetch(`${API}/hipotecarias-marca/${h.id}`, { method: 'DELETE' });
    const data = await r.json();
    if (!r.ok) { window.alert(data.error || 'No se pudo eliminar'); return; }
    cargar();
  };

  return (
    <div style={st.wrap}>
      <div style={st.card}>
        <h3 style={st.title}>Hipotecarias / Marca</h3>
        <p style={st.subtitle}>
          Logo (arriba a la derecha) y color del título usados en el PDF del Comparativo de Tasas,
          según la hipotecaria ligada al broker que generó la cotización (catálogo → Brokers).
        </p>
        {!showForm && (
          <div style={{ marginBottom: 12 }}>
            <button style={{ ...st.btn, ...st.btnPrimary }} onClick={abrirNuevo}>+ Nueva Hipotecaria</button>
          </div>
        )}

        {showForm && (
          <div style={{ ...st.card, backgroundColor: 'white', marginBottom: 16 }}>
            <h4 style={{ marginTop: 0, marginBottom: 12 }}>{editando ? 'Editar' : 'Nueva'} Hipotecaria</h4>
            <div style={st.row}>
              <div style={{ ...st.field, flex: '1 1 220px' }}>
                <label style={st.label}>Nombre</label>
                <input style={st.input} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div style={{ ...st.field, flex: '0 0 170px' }}>
                <label style={st.label}>Color del texto del título</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="color" value={HEX_RE.test(form.color_texto_titulo) ? form.color_texto_titulo : '#1a3a6e'}
                    onChange={(e) => setForm({ ...form, color_texto_titulo: e.target.value })}
                    style={{ width: 40, height: 36, padding: 2, border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }} />
                  <input style={{ ...st.input, width: 100 }} value={form.color_texto_titulo}
                    onChange={(e) => setForm({ ...form, color_texto_titulo: e.target.value })} placeholder="#1a3a6e" />
                </div>
              </div>
              <div style={{ ...st.field, flex: '0 0 90px' }}>
                <label style={st.label}>Activo</label>
                <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} style={{ width: 20, height: 20 }} />
              </div>
            </div>
            <div style={st.field}>
              <label style={st.label}>Logo (PNG, JPG o SVG — se muestra arriba a la derecha del PDF)</label>
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onLogoChange} />
              {logoPreviewUrl && <img src={logoPreviewUrl} alt="Logo" style={st.logoPreview} />}
            </div>
            {error && <div style={st.error}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...st.btn, ...st.btnPrimary }} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</button>
              <button style={{ ...st.btn, ...st.btnSecondary }} onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </div>
        )}

        {!showForm && (
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Logo</th>
                <th style={st.th}>Nombre</th>
                <th style={st.th}>Color de título</th>
                <th style={st.th}>Activo</th>
                <th style={{ ...st.th, textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((h) => (
                <tr key={h.id}>
                  <td style={st.td}>
                    {logoUrls[h.id] ? <img src={logoUrls[h.id]} alt={h.nombre} style={{ maxHeight: 26, maxWidth: 110 }} /> : '—'}
                  </td>
                  <td style={{ ...st.td, fontWeight: '600' }}>{h.nombre}</td>
                  <td style={st.td}><span style={{ ...st.swatch, backgroundColor: h.color_texto_titulo }}></span>{h.color_texto_titulo}</td>
                  <td style={st.td}>{h.activo ? 'Sí' : 'No'}</td>
                  <td style={{ ...st.td, textAlign: 'center' }}>
                    <button onClick={() => abrirEditar(h)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>Editar</button>
                    <button onClick={() => eliminar(h)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Eliminar</button>
                  </td>
                </tr>
              ))}
              {!lista.length && (
                <tr><td colSpan={5} style={{ ...st.td, textAlign: 'center', color: '#6b7280' }}>Sin hipotecarias capturadas.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default HipotecariasMarca;
